import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { Client } from '../../../../context/ClientContext';
import { Button, Checkbox, Input, SegmentedControl, Table, TableColumn, useToast } from '../../../ui';
import { supabase } from '../../../../lib/supabase';
import type { Json } from '../../../../lib/database.types';
import OpportunityDetailPanel from './OpportunityDetailPanel';
import { faint, formatRelative, linkStyle, urlPath } from './format';
import { CountLabel, DRBadge, ReasonField, RelevanceMeter, StatusBadge, TypeLabel } from './shared';

type Opportunity = {
  id: string;
  opportunity_type: string;
  target_url: string;
  target_domain: string;
  anchor_suggestion: string | null;
  target_page_url: string | null;
  target_dr: number | null;
  target_traffic: number | null;
  relevance_score: number | null;
  source: string;
  discovered_via: Json | null;
  brief: Json | null;
  status: string;
  status_reason: string | null;
  outreach_date: string | null;
  response_date: string | null;
  acquired_link_id: string | null;
  assigned_to: string | null;
  notes: string | null;
  created_at: string;
};

type StatusFilter = 'all' | 'new' | 'approved' | 'pitched' | 'won' | 'declined_ignored';

const FILTERS: Record<StatusFilter, (r: Opportunity) => boolean> = {
  all: () => true,
  new: (r) => r.status === 'new',
  approved: (r) => r.status === 'approved',
  pitched: (r) => ['outreach_drafted', 'pitched'].includes(r.status),
  won: (r) => r.status === 'won',
  declined_ignored: (r) => ['declined', 'ignored'].includes(r.status),
};

export default function BacklinksOpportunities({ client }: { client: Client }) {
  const toast = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const [rows, setRows] = useState<Opportunity[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [ignoring, setIgnoring] = useState(false);

  const selectedId = searchParams.get('opportunity');
  const setSelectedId = (id: string | null) => {
    const next = new URLSearchParams(searchParams);
    if (id) next.set('opportunity', id);
    else next.delete('opportunity');
    setSearchParams(next);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('backlink_opportunities')
        .select('*')
        .eq('client_id', client.id)
        .order('target_dr', { ascending: false, nullsFirst: false })
        .order('created_at', { ascending: false });

      if (error) throw error;
      setRows(data || []);
    } catch (err) {
      console.error('Error loading opportunities:', err);
      toast.error('Failed to load opportunities', err instanceof Error ? err.message : undefined);
      setRows([]); // Set empty array on error to avoid undefined issues
    } finally {
      setLoading(false);
    }
  }, [client.id, toast]);

  useEffect(() => {
    load();
  }, [load]);

  const counts = useMemo(
    () =>
      Object.fromEntries((Object.keys(FILTERS) as StatusFilter[]).map((f) => [f, rows.filter(FILTERS[f]).length])) as Record<StatusFilter, number>,
    [rows],
  );

  const filtered = rows.filter((row) => {
    if (!FILTERS[statusFilter](row)) return false;
    if (search) {
      const q = search.toLowerCase();
      if (!row.target_domain.toLowerCase().includes(q) && !row.target_url.toLowerCase().includes(q) && !row.opportunity_type.toLowerCase().includes(q)) {
        return false;
      }
    }
    return true;
  });

  // Only new opportunities can be bulk-approved or ignored.
  const selectable = filtered.filter((r) => r.status === 'new');
  const allSelected = selectable.length > 0 && selectable.every((r) => selected.has(r.id));

  const clearSelection = () => {
    setSelected(new Set());
    setIgnoring(false);
  };

  const bulkUpdate = async (patch: { status: string; status_reason?: string }, verb: string) => {
    const n = selected.size;
    try {
      const { error } = await supabase.from('backlink_opportunities').update(patch).in('id', Array.from(selected)).eq('status', 'new');
      if (error) throw error;
      toast.success(`${verb} ${n} ${n === 1 ? 'opportunity' : 'opportunities'}`);
      clearSelection();
      load();
    } catch (err) {
      console.error(`Error (${verb}) opportunities:`, err);
      toast.error(`Failed: ${verb.toLowerCase()}`, err instanceof Error ? err.message : undefined);
    }
  };

  const columns: TableColumn<Opportunity>[] = [
    {
      key: 'select',
      width: '44px',
      label: (
        <Checkbox
          checked={allSelected}
          disabled={!selectable.length}
          onChange={(checked) => setSelected(checked ? new Set(selectable.map((r) => r.id)) : new Set())}
        />
      ),
      render: (_value, row) =>
        row.status === 'new' ? (
          <span onClick={(e) => e.stopPropagation()}>
            <Checkbox
              checked={selected.has(row.id)}
              onChange={(checked) => {
                const next = new Set(selected);
                if (checked) next.add(row.id);
                else next.delete(row.id);
                setSelected(next);
              }}
            />
          </span>
        ) : null,
    },
    {
      key: 'target_domain',
      label: 'Site',
      width: '30%',
      render: (_value, row) => (
        <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <a
            href={row.target_url}
            target="_blank"
            rel="noopener noreferrer"
            style={{ ...linkStyle, fontWeight: 'var(--weight-medium)' as React.CSSProperties['fontWeight'], overflow: 'hidden', textOverflow: 'ellipsis' }}
            onClick={(e) => e.stopPropagation()}
          >
            {row.target_domain}
          </a>
          <span style={{ ...faint, fontSize: 'var(--text-xs)', overflow: 'hidden', textOverflow: 'ellipsis' }} title={row.target_url}>
            {urlPath(row.target_url)}
          </span>
        </div>
      ),
    },
    { key: 'target_dr', label: 'DR', width: '8%', align: 'center', render: (_value, row) => <DRBadge dr={row.target_dr} /> },
    { key: 'opportunity_type', label: 'Type', width: '17%', render: (_value, row) => <TypeLabel type={row.opportunity_type} /> },
    { key: 'relevance_score', label: 'Relevance', width: '13%', render: (_value, row) => <RelevanceMeter score={row.relevance_score} /> },
    { key: 'status', label: 'Status', width: '13%', render: (_value, row) => <StatusBadge status={row.status} /> },
    {
      key: 'created_at',
      label: 'Found',
      width: '12%',
      align: 'right',
      render: (_value, row) => <span style={{ color: 'var(--text-muted)', fontSize: 'var(--text-sm)' }}>{formatRelative(row.created_at)}</span>,
    },
  ];

  return (
    <>
      <div style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'center', flexWrap: 'wrap', marginBottom: 'var(--space-4)' }}>
        <SegmentedControl
          value={statusFilter}
          onChange={(id) => {
            setStatusFilter(id as StatusFilter);
            clearSelection();
          }}
          options={[
            { id: 'all', label: <CountLabel label="All" n={counts.all} /> },
            { id: 'new', label: <CountLabel label="New" n={counts.new} /> },
            { id: 'approved', label: <CountLabel label="Approved" n={counts.approved} /> },
            { id: 'pitched', label: <CountLabel label="Pitched" n={counts.pitched} /> },
            { id: 'won', label: <CountLabel label="Won" n={counts.won} /> },
            { id: 'declined_ignored', label: <CountLabel label="Passed" n={counts.declined_ignored} /> },
          ]}
          style={{ maxWidth: '100%', overflowX: 'auto' }}
        />
        <span style={{ flex: 1 }} />
        <div style={{ width: '260px' }}>
          <Input icon="search" fieldSize="sm" placeholder="Search site or type" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
      </div>

      {selected.size > 0 && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-3)',
            padding: '8px 8px 8px 16px',
            marginBottom: 'var(--space-3)',
            background: 'var(--accent-soft)',
            border: '1px solid var(--border-hairline)',
            borderRadius: 'var(--radius-md)',
          }}
        >
          {ignoring ? (
            <ReasonField
              placeholder={`Why ignore ${selected.size === 1 ? 'this one' : `these ${selected.size}`}? (feeds the deny rules)`}
              confirmLabel={`Ignore ${selected.size}`}
              onConfirm={(reason) => bulkUpdate({ status: 'ignored', status_reason: reason }, 'Ignored')}
              onCancel={() => setIgnoring(false)}
            />
          ) : (
            <>
              <span style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--weight-medium)' as React.CSSProperties['fontWeight'] }}>
                {selected.size} selected
              </span>
              <span style={{ flex: 1 }} />
              <Button size="sm" variant="ghost" onClick={clearSelection}>
                Clear
              </Button>
              <Button size="sm" variant="secondary" icon="x" onClick={() => setIgnoring(true)}>
                Ignore
              </Button>
              <Button size="sm" variant="primary" icon="check" onClick={() => bulkUpdate({ status: 'approved' }, 'Approved')}>
                Approve
              </Button>
            </>
          )}
        </div>
      )}

      <Table
        rows={filtered}
        columns={columns}
        emptyMessage={
          <span style={faint}>
            {loading ? 'Loading…' : rows.length ? 'No opportunities match these filters.' : 'No opportunities yet. Discovery runs drop them here.'}
          </span>
        }
        onRowClick={(row) => setSelectedId(row.id)}
        rowStyle={(row) => (selected.has(row.id) ? { background: 'var(--surface-active)' } : ['declined', 'ignored'].includes(row.status) ? { opacity: 0.6 } : undefined)}
      />

      {selectedId && <OpportunityDetailPanel opportunityId={selectedId} onClose={() => setSelectedId(null)} onUpdate={load} />}
    </>
  );
}
